"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ClipboardList, Loader2 } from "lucide-react";
import { aiModelPricingApi } from "@/lib/aiModelPricing";

interface Props {
  tier: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Renders the per-tier human guide (Markdown) as a side panel.
 * Backed by `GET /api/admin/ai-model-pricing/pricing/guide?tier=…`.
 */
export function StepByStepGuide({ tier, open, onClose }: Props) {
  const [markdown, setMarkdown] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    aiModelPricingApi
      .guide(tier)
      .then(setMarkdown)
      .catch((e) => setError(e?.message ?? "Failed to load guide"))
      .finally(() => setLoading(false));
  }, [open, tier]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
            className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-[#0A0A0A] border-l border-[rgba(255,255,255,0.08)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-[rgba(255,255,255,0.06)] p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#FFD60A]/15 text-[#FFD60A] flex items-center justify-center">
                  <ClipboardList className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#8E8E93]">
                    Push new price
                  </div>
                  <h2 className="text-base font-bold text-[#FAFAFA] tracking-tight">
                    Tier: {tier}
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-[#8E8E93] hover:bg-white/5 hover:text-[#FAFAFA] transition"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 text-sm text-[#FAFAFA] leading-relaxed">
              {loading && (
                <div className="flex items-center gap-2 text-[#8E8E93]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading guide…
                </div>
              )}
              {error && <p className="text-[#E63946]">{error}</p>}
              {!loading && !error && (
                <pre className="whitespace-pre-wrap font-sans text-[13px] text-[#FAFAFA]/90 leading-relaxed">
                  {markdown}
                </pre>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
